/**
 * Usage reporting contract.
 *
 * Each environment scans the provider CLIs' own on-disk session transcripts
 * (`~/.claude/projects/**\/*.jsonl`, `~/.codex/sessions/**\/*.jsonl`) rather than
 * relying on Cleo's own orchestration projections, so usage stays complete
 * even for turns that were never driven through Cleo. This mirrors the
 * approach `ccusage` takes.
 *
 * Environments return pre-aggregated `(day, hourStart?, provider, model)`
 * buckets. Raw transcript records never cross the wire.
 *
 * @module usage
 */
import * as Schema from "effect/Schema";

import { NonNegativeInt, TrimmedNonEmptyString } from "./baseSchemas.ts";

/**
 * Bumped whenever the shape of {@link UsageSummary} changes incompatibly. The
 * client renders partial coverage when an environment reports an older version
 * rather than failing the whole page.
 */
export const USAGE_CONTRACT_VERSION = 5 as const;

/**
 * Providers whose usage this contract can carry. Claude and Codex are scanned
 * from transcript files the CLIs write locally. (Cursor, whose usage was
 * pulled from its dashboard API as `providerReported` cost, left the estate
 * on 2026-09-18 at the principal's word.)
 */
export const UsageProviderKind = Schema.Literals(["claude", "codex"]);
export type UsageProviderKind = typeof UsageProviderKind.Type;

/**
 * A calendar day in the reporting time zone, formatted `YYYY-MM-DD`.
 *
 * Days are bucketed server-side so that a turn always lands on the day the user
 * experienced it, not the UTC day.
 */
const USAGE_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const UsageDay = TrimmedNonEmptyString.check(Schema.isPattern(USAGE_DAY_PATTERN)).pipe(
  Schema.brand("UsageDay"),
);
export type UsageDay = typeof UsageDay.Type;

export const UsageResolution = Schema.Literals(["day", "hour"]);
export type UsageResolution = typeof UsageResolution.Type;

/**
 * Why a bucket's cost is what it is.
 *
 * - `providerReported` - the transcript carried an explicit cost figure.
 * - `modelPriced` - we matched the model against the LiteLLM rate table.
 * - `unpriced` - tokens are known, rates are not. Counted in totals, excluded
 *   from cost.
 */
export const UsageCostSource = Schema.Literals(["providerReported", "modelPriced", "unpriced"]);
export type UsageCostSource = typeof UsageCostSource.Type;

/**
 * Token counts for a bucket.
 *
 * `cachedInputTokens` and `cacheCreationTokens` are disjoint from
 * `uncachedInputTokens`; summing all three gives total input.
 * `cacheCreationOneHourTokens` is the one-hour TTL subset of
 * `cacheCreationTokens`, present when the provider breaks it out, so it must
 * never be added on top. Likewise, `reasoningTokens` is a subset of
 * `outputTokens`.
 */
export const UsageTokenTotals = Schema.Struct({
  uncachedInputTokens: NonNegativeInt,
  cachedInputTokens: NonNegativeInt,
  cacheCreationTokens: NonNegativeInt,
  cacheCreationOneHourTokens: Schema.optional(NonNegativeInt),
  outputTokens: NonNegativeInt,
  reasoningTokens: NonNegativeInt,
});
export type UsageTokenTotals = typeof UsageTokenTotals.Type;

/**
 * One `(day, hourStart?, provider, model)` cell. `hourStart` is the UTC start
 * instant of a rolling bucket and is present only for hourly requests.
 *
 * `costUsd` is the raw API-equivalent cost of these tokens. It is not money
 * spent: subscription plans bill separately. `unpricedRecords` counts records
 * whose tokens are included in the token totals but which contributed nothing
 * to `costUsd`.
 */
export const UsageBucket = Schema.Struct({
  day: UsageDay,
  hourStart: Schema.optional(TrimmedNonEmptyString),
  provider: UsageProviderKind,
  model: TrimmedNonEmptyString,
  /**
   * Key of the scan source (the provider instance) whose transcripts produced
   * this bucket. Stamped on every bucket since contract v5; optional in the
   * schema only so older payloads still decode — the merge layer version-gates
   * mixed cohorts.
   */
  sourceKey: Schema.optional(TrimmedNonEmptyString),
  totals: UsageTokenTotals,
  costUsd: Schema.Number,
  /**
   * What the cached input would have cost at full input rates minus what it
   * actually cost. Requires the rate table, so it is computed alongside cost
   * rather than derived on the client.
   */
  cacheSavingsUsd: Schema.Number,
  costSource: UsageCostSource,
  /** Distinct assistant responses, after de-duplication. */
  records: NonNegativeInt,
  unpricedRecords: NonNegativeInt,
  /** Distinct transcript sessions that contributed to this cell. */
  sessions: NonNegativeInt,
});
export type UsageBucket = typeof UsageBucket.Type;

/**
 * Identifies the physical transcript directory a source read from.
 *
 * Two environments on the same machine (worktree servers, for example) resolve
 * the same provider home and would otherwise double count. The client drops
 * duplicate fingerprints before merging.
 */
export const UsageSourceFingerprint = Schema.Struct({
  hostId: TrimmedNonEmptyString,
  provider: UsageProviderKind,
  resolvedHomePath: TrimmedNonEmptyString,
  /**
   * Filesystem identity of the transcript directory, as `device:inode`.
   *
   * Hostname and path alone are not enough: every Mac in a fleet resolves
   * `/Users/<user>/.claude`, so two machines that happen to share a hostname
   * would look like one source and have their usage silently dropped. The
   * device/inode pair is stable for two servers reading the same directory and
   * effectively never collides across machines. Empty when it cannot be read.
   */
  volumeId: Schema.String,
});
export type UsageSourceFingerprint = typeof UsageSourceFingerprint.Type;

export const UsageSourceStatus = Schema.Literals(["ok", "missing", "partial", "failed"]);
export type UsageSourceStatus = typeof UsageSourceStatus.Type;

/**
 * Plan-capacity saturation for the ACCOUNT behind a source, sampled from
 * Claude Desktop's local plan-usage telemetry. Percentages are 0-100 as the
 * provider reports them; they describe subscription-window saturation, not
 * cost. `sampledAt` matters: the sampler only runs while the desktop app has
 * that account active, so a stale timestamp means "no recent signal", never
 * "idle". Optional and provider-specific (Claude only today) — a compatible
 * v5 addition, environments that predate it simply omit the field.
 */
export const UsageSourceCapacity = Schema.Struct({
  fiveHourPercent: Schema.Number,
  sevenDayPercent: Schema.Number,
  extraUsagePercent: Schema.Number,
  /** ISO 8601 timestamp of the newest sample for this account. */
  sampledAt: TrimmedNonEmptyString,
});
export type UsageSourceCapacity = typeof UsageSourceCapacity.Type;

/**
 * The account the PROVIDER says is signed in to this source's home, as opposed
 * to the display name someone typed into settings.
 *
 * The two drift: an instance labelled "work@example.com" keeps that label
 * after its home is re-authenticated as someone else, and the usage rows then
 * name an account that produced none of the tokens. Read from the provider
 * rather than from configuration, this is the only identity that can be
 * trusted to name the currently signed-in account. Historical transcripts do
 * not carry account identity, so this must not be read as proof that every
 * older record in a reused home belonged to that account.
 */
export const UsageSourceAccount = Schema.Struct({
  /** Account identity as the provider spells it. */
  email: Schema.optional(TrimmedNonEmptyString),
  /** Provider's own plan name ("pro", "max"), unnormalised. */
  planType: Schema.optional(TrimmedNonEmptyString),
});
export type UsageSourceAccount = typeof UsageSourceAccount.Type;

/** One day of provider-reported token activity for an account. */
export const UsageProviderDay = Schema.Struct({
  day: UsageDay,
  tokens: NonNegativeInt,
});
export type UsageProviderDay = typeof UsageProviderDay.Type;

/**
 * What the PROVIDER reports this account consumed, independent of any
 * transcript on this machine.
 *
 * This is not a second opinion about the same tokens and must never be summed
 * with the scan: it counts the account's activity across every device and
 * surface, while the scan counts the transcripts this machine happens to hold.
 * Its purpose is the gap between them — usage that exists but is attributable
 * to no local transcript, which a transcript-only report reads as zero.
 *
 * Days the account was quiet are absent rather than zero, so the payload stays
 * proportional to activity. `sampledAt` is when the provider was asked: the
 * read is a network call made off the scan's critical path, so a stale
 * timestamp means "not asked recently", never "idle".
 */
export const UsageProviderUsage = Schema.Struct({
  daily: Schema.Array(UsageProviderDay),
  sampledAt: TrimmedNonEmptyString,
  /** Account-lifetime tokens, when the provider reports one. */
  lifetimeTokens: Schema.optional(NonNegativeInt),
});
export type UsageProviderUsage = typeof UsageProviderUsage.Type;

/** One independently metered provider quota window. */
export const UsageRateLimitWindow = Schema.Struct({
  /** Provider limit id, or `default` for the legacy single-bucket response. */
  limitId: TrimmedNonEmptyString,
  /** Provider display name, when it supplies one. */
  limitName: Schema.optional(TrimmedNonEmptyString),
  window: Schema.Literals(["primary", "secondary"]),
  usedPercent: Schema.Number,
  windowDurationMinutes: Schema.optional(NonNegativeInt),
  /** Unix timestamp in seconds, exactly as the provider reports it. */
  resetsAtEpochSeconds: Schema.optional(NonNegativeInt),
  /**
   * The ordinary model whose display name and reasoning options describe this
   * quota alias (SAU-CAN-23). Codex reports it for a reserve bucket; it is
   * metadata for the alias, never a replacement for the requested model.
   */
  normalModelSlug: Schema.optional(TrimmedNonEmptyString),
});
export type UsageRateLimitWindow = typeof UsageRateLimitWindow.Type;

/**
 * Provider-reported quota saturation for one account.
 *
 * The sample time is deliberately inseparable from the windows: an old quota
 * is still useful context, but it is not a live near-capacity warning.
 */
export const UsageSourceRateLimits = Schema.Struct({
  windows: Schema.Array(UsageRateLimitWindow),
  sampledAt: TrimmedNonEmptyString,
  source: Schema.optional(Schema.Literals(["claude-code", "codex-app-server"])),
  /**
   * Whether the provider will serve ordinary models right now (SAU-CAN-23).
   * Absent means the provider did not answer — never availability, and never a
   * value a percentage or a reset time may stand in for. Only a full read
   * carries it; a sparse rolling update never does.
   */
  ordinaryUsageAllowed: Schema.optional(Schema.Boolean),
  /**
   * The provider's own banner for this state, verbatim (for example
   * `luna_reserve` or `pro_rate_limit_reached`). It is the authority for which
   * remedy the provider is offering; the fold reads it, never guesses it.
   */
  upsellBannerType: Schema.optional(TrimmedNonEmptyString),
});
export type UsageSourceRateLimits = typeof UsageSourceRateLimits.Type;

/** Native account probe state, independent of retained historical usage. */
export const UsageSourceAccountHealth = Schema.Struct({
  status: Schema.Literals([
    "checking",
    "connected",
    "needs_sign_in",
    "unavailable",
    "unsupported",
    "disabled",
  ]),
  checkedAt: TrimmedNonEmptyString,
  authentication: Schema.optional(
    Schema.Struct({
      status: Schema.Literals([
        "verified",
        "rejected",
        "rate-limited",
        "unavailable",
        "identity-mismatch",
        "unverified",
        "stale",
        "unknown",
      ]),
      observedAt: Schema.optional(TrimmedNonEmptyString),
      credentialRef: TrimmedNonEmptyString,
    }),
  ),
  identity: Schema.optional(
    Schema.Struct({
      status: Schema.Literals(["matched", "mismatch", "unknown"]),
      observedAt: TrimmedNonEmptyString,
      accountUuid: TrimmedNonEmptyString,
    }),
  ),
  reason: Schema.optional(
    Schema.Literals(["timeout", "native_client_error", "quota_unavailable", "not_signed_in"]),
  ),
});
export type UsageSourceAccountHealth = typeof UsageSourceAccountHealth.Type;

export const UsageSource = Schema.Struct({
  fingerprint: UsageSourceFingerprint,
  /**
   * Key of the provider instance this source scanned for, matching
   * {@link UsageBucket}.sourceKey. Contract v5+.
   */
  sourceKey: Schema.optional(TrimmedNonEmptyString),
  /** Human label for the instance: its display name ("work@example.com"). */
  label: Schema.optional(TrimmedNonEmptyString),
  status: UsageSourceStatus,
  scannedFiles: NonNegativeInt,
  skippedFiles: NonNegativeInt,
  /** Records that parsed but carried no recognisable usage payload. */
  malformedRecords: NonNegativeInt,
  /**
   * Distinct transcript sessions seen under this directory. Buckets also carry
   * per-bucket session counts, but a session spans days and models, so summing
   * those overcounts; this is the figure clients should total.
   */
  distinctSessions: NonNegativeInt,
  message: Schema.NullOr(TrimmedNonEmptyString),
  /** Subscription-window saturation for this account, when locally sampled. */
  capacity: Schema.optional(UsageSourceCapacity),
  /** Who the provider says is signed in here. Absent when unread. */
  account: Schema.optional(UsageSourceAccount),
  accountHealth: Schema.optional(UsageSourceAccountHealth),
  /** The provider's own account-wide totals. Absent when unread. */
  providerUsage: Schema.optional(UsageProviderUsage),
  /** Provider quota windows. Absent when the provider did not answer. */
  rateLimits: Schema.optional(UsageSourceRateLimits),
});
export type UsageSource = typeof UsageSource.Type;

export const UsagePricingStatus = Schema.Literals(["fresh", "cached", "unavailable"]);
export type UsagePricingStatus = typeof UsagePricingStatus.Type;

/**
 * Provenance for the rate table, so the UI can be honest about how good the
 * cost figures are.
 */
export const UsagePricing = Schema.Struct({
  status: UsagePricingStatus,
  source: TrimmedNonEmptyString,
  fetchedAt: Schema.NullOr(Schema.String),
  knownModels: NonNegativeInt,
});
export type UsagePricing = typeof UsagePricing.Type;

/**
 * One provider window as the capacity fold carries it (SAU-CAN-17). `scope`
 * says what the window governs: every model on the account, one model family
 * (Claude's family-scoped weekly caps, Codex's per-model limits), or paid extra
 * usage. `source` names which observation supplied the value.
 */
export const UsageCapacityWindow = Schema.Struct({
  limitId: TrimmedNonEmptyString,
  limitName: Schema.optional(TrimmedNonEmptyString),
  scope: Schema.Literals(["all", "family", "extra", "reserve"]),
  family: Schema.optional(TrimmedNonEmptyString),
  window: Schema.Literals(["primary", "secondary"]),
  usedPercent: Schema.Number,
  windowDurationMinutes: Schema.optional(NonNegativeInt),
  resetsAtEpochSeconds: Schema.optional(NonNegativeInt),
  /** The ordinary model this alias borrows its metadata from, when the provider names one. */
  normalModelSlug: Schema.optional(TrimmedNonEmptyString),
  sampledAt: TrimmedNonEmptyString,
  source: Schema.Literals(["native-snapshot", "live-event"]),
});
export type UsageCapacityWindow = typeof UsageCapacityWindow.Type;

export const UsageCapacityFreshness = Schema.Literals(["fresh", "stale", "unknown"]);
export type UsageCapacityFreshness = typeof UsageCapacityFreshness.Type;
export const UsageCapacityStatus = Schema.Literals([
  "available",
  "near-limit",
  /**
   * Ordinary usage is spent and the provider named a reserve model that still
   * has capacity (SAU-CAN-23). The account can still take work; it is not
   * exhausted, and it is not ordinarily available either.
   */
  "reserve-only",
  "exhausted",
  "unknown",
]);
export type UsageCapacityStatus = typeof UsageCapacityStatus.Type;

/**
 * One span of burn (SAU-CAN-21): how many points of the weekly window's used
 * percent were gained since the oldest provider sample inside the span, the
 * provider's own percentages of one window — never tokens or an estimate.
 */
export const UsageCapacityBurnSpan = Schema.Struct({
  deltaPercent: Schema.Number,
  /** The sample the delta was measured since, ISO 8601. */
  since: TrimmedNonEmptyString,
  /** Whether the samples reach back over the whole span. */
  complete: Schema.Boolean,
});
export type UsageCapacityBurnSpan = typeof UsageCapacityBurnSpan.Type;

export const UsageCapacityBurn = Schema.Struct({
  lastHour: Schema.NullOr(UsageCapacityBurnSpan),
  lastDay: Schema.NullOr(UsageCapacityBurnSpan),
});
export type UsageCapacityBurn = typeof UsageCapacityBurn.Type;

/**
 * One capacity record per stable subscription account. Homes proving the same
 * provider identity fold into one record; a connection whose provider exposes
 * no subject stays its own record with `identityProven: false`.
 */
export const UsageAccountCapacity = Schema.Struct({
  accountId: TrimmedNonEmptyString,
  provider: Schema.Literals(["claude", "codex"]),
  identityProven: Schema.Boolean,
  identity: Schema.Struct({
    email: Schema.optional(TrimmedNonEmptyString),
    planType: Schema.optional(TrimmedNonEmptyString),
    accountUuid: Schema.optional(TrimmedNonEmptyString),
    organizationUuid: Schema.optional(TrimmedNonEmptyString),
  }),
  instanceIds: Schema.Array(TrimmedNonEmptyString),
  homes: Schema.Array(Schema.String),
  enabled: Schema.Boolean,
  health: Schema.optional(UsageSourceAccountHealth),
  capacity: Schema.Struct({
    freshness: UsageCapacityFreshness,
    /** Worst account-wide window; family windows are judged per requested family. */
    status: UsageCapacityStatus,
    /**
     * The family of the model this account runs by default, as its provider's
     * catalog names it (SAU-CAN-19, dated revision 2026-09-21). The dispatch
     * order ranks by this family's window when the provider reports one.
     * Absent when the provider scopes no window by family (Codex) or no
     * catalog answers for the account.
     */
    defaultModelFamily: Schema.optional(TrimmedNonEmptyString),
    windows: Schema.Array(UsageCapacityWindow),
    sampledAt: Schema.NullOr(Schema.String),
    attemptedAt: Schema.NullOr(Schema.String),
    reason: Schema.NullOr(Schema.String),
    /**
     * The reserve model slug the provider named, present only while the status
     * is `reserve-only` (SAU-CAN-23). It is the only model this account will
     * serve until ordinary usage returns.
     */
    reserveModelSlug: Schema.optional(TrimmedNonEmptyString),
    /** Burn of the weekly window over the last hour and day; absent without a weekly window. */
    burn: Schema.optional(UsageCapacityBurn),
  }),
  lastRefusal: Schema.optional(
    Schema.Struct({
      at: TrimmedNonEmptyString,
      resetsAtEpochSeconds: Schema.optional(NonNegativeInt),
      detail: Schema.optional(TrimmedNonEmptyString),
    }),
  ),
});
export type UsageAccountCapacity = typeof UsageAccountCapacity.Type;

export const UsageAccountCapacityReport = Schema.Struct({
  contractVersion: Schema.Number,
  readAt: Schema.String,
  accounts: Schema.Array(UsageAccountCapacity),
});
export type UsageAccountCapacityReport = typeof UsageAccountCapacityReport.Type;

export const UsageSummaryInput = Schema.Struct({
  /** Inclusive first day of the window, in `timeZone`. */
  sinceDay: UsageDay,
  /** Inclusive last day of the window, in `timeZone`. */
  untilDay: UsageDay,
  /**
   * IANA zone the client wants days bucketed in. An offset would be wrong for
   * any window that crosses a DST boundary.
   */
  timeZone: TrimmedNonEmptyString,
  /** Defaults to daily for older clients. */
  resolution: Schema.optional(UsageResolution),
  /** Inclusive UTC instant for an hourly rolling window. */
  sinceTime: Schema.optional(TrimmedNonEmptyString),
  /** Exclusive UTC instant for an hourly rolling window. */
  untilTime: Schema.optional(TrimmedNonEmptyString),
});
export type UsageSummaryInput = typeof UsageSummaryInput.Type;

export const UsageSummary = Schema.Struct({
  contractVersion: Schema.Number,
  readAt: Schema.String,
  timeZone: TrimmedNonEmptyString,
  sinceDay: UsageDay,
  untilDay: UsageDay,
  buckets: Schema.Array(UsageBucket),
  sources: Schema.Array(UsageSource),
  pricing: UsagePricing,
  /** Wall-clock cost of the scan, surfaced in diagnostics. */
  scanDurationMs: NonNegativeInt,
});
export type UsageSummary = typeof UsageSummary.Type;

export class UsageReadError extends Schema.TaggedErrorClass<UsageReadError>()("UsageReadError", {
  reason: Schema.Literals(["scanFailed", "invalidWindow"]),
  /** Stable, bounded description. The underlying failure travels in `cause`. */
  detail: TrimmedNonEmptyString,
  cause: Schema.optional(Schema.Defect()),
}) {
  override get message(): string {
    return `Usage read failed (${this.reason}): ${this.detail}`;
  }
}
